import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Input, Space } from "antd";
import { get, post, put, del } from "../API/api_helper";
import Swal from "sweetalert2";
import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";

interface Grade {
  id: number;
  grade: string;
}

interface GradeModalProps {
  show: boolean;
  onHide: () => void;
  editingGrade?: Grade | null; // <-- if provided, we're editing
  onSaved: () => void; // callback to refresh main table after add/edit
}

const GradeModal: React.FC<GradeModalProps> = ({ show, onHide, editingGrade, onSaved }) => {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [gradeValue, setGradeValue] = useState("");

  // Load all grades (used to show list inside modal)
  const fetchGrades = async () => {
    try {
      const res = await get("/grades");
      setGrades(res.data || res);
    } catch (err) {
      console.error("Failed to fetch grades", err);
    }
  };

  useEffect(() => {
    if (show) {
      fetchGrades();
      setGradeValue(editingGrade ? editingGrade.grade : ""); // pre-fill for edit
    }
  }, [show, editingGrade]);

  // Add or update grade
  const handleSave = async () => {
    if (!gradeValue.trim()) {
      Swal.fire("Error", "Please enter a grade", "error");
      return;
    }

    try {
      if (editingGrade) {
        // Update existing grade
        await put(`/grades/${editingGrade.id}`, { grade: gradeValue.trim() });
        Swal.fire("Success", "Grade updated successfully", "success");
      } else {
        // Add new grade
        await post("/grades", { grade: gradeValue.trim() });
        Swal.fire("Success", "Grade added successfully", "success");
      }

      setGradeValue("");
      fetchGrades();
      onSaved(); // refresh parent table
    } catch (err) {
      Swal.fire("Error", "Failed to save grade", "error");
    }
  };

  const handleDelete = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This grade will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (confirm.isConfirmed) {
      try {
        await del(`/grades/${id}`);
        Swal.fire("Deleted!", "Grade deleted.", "success");
        fetchGrades();
        onSaved();
      } catch (err) {
        Swal.fire("Error", "Failed to delete grade", "error");
      }
    }
  };

  const columns = [
    { title: "Grade", dataIndex: "grade" },
    {
      title: "Actions",
      render: (_: any, record: Grade) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(record.id)}
        />
      ),
    },
  ];

  return (
    <Modal
      open={show}
      onCancel={onHide}
      footer={null}
      centered
      title={editingGrade ? "Edit Grade" : "Manage Grades"}
    >
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Input
          placeholder="Enter Grade (e.g. LKG, UKG, 1, 2)"
          value={gradeValue}
          onChange={(e) => setGradeValue(e.target.value)}
          type="text"
          style={{ width: "100%" }}
        />

        <Button
          type="primary"
          icon={editingGrade ? <SaveOutlined /> : <PlusOutlined />}
          onClick={handleSave}
        >
          {editingGrade ? "Save" : "Add"}
        </Button>
      </div>


    </Modal>
  );
};

export default GradeModal;
